import { useEffect } from "react";

export const SITE_URL = "https://www.beyondboardwood.com";
export const SITE_NAME = "Beyond Board Woodworking";

const DEFAULT_IMAGE = `${SITE_URL}/branding/bbw-logo.jpg`;
const JSONLD_ATTR = "data-seo-jsonld";

function absoluteUrl(url) {
  if (!url) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}

function upsertMeta(attribute, key, content) {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * Declarative per-page SEO: document title, meta description, canonical URL,
 * Open Graph + Twitter/X tags, and optional JSON-LD structured data.
 * Renders nothing and never touches visual layout.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Custom Laser-Cut Basswood Signs & Decor`;
  const canonical = `${SITE_URL}${path}`;
  const imageUrl = absoluteUrl(image);
  const serializedLd = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertCanonical(canonical);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", imageUrl);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    document.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach((node) => node.remove());
    if (serializedLd) {
      const entries = JSON.parse(serializedLd);
      (Array.isArray(entries) ? entries : [entries]).forEach((entry) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(JSONLD_ATTR, "true");
        script.textContent = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }
    return () => {
      document.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach((node) => node.remove());
    };
  }, [fullTitle, description, canonical, imageUrl, type, noindex, serializedLd]);

  return null;
}
